import React from 'react';
import { motion } from 'framer-motion';
import { FiKey, FiAlertTriangle, FiCheckCircle, FiEye, FiGitBranch, FiTrendingUp, FiSettings, FiLock } from 'react-icons/fi';
import ScrollHeader from '../components/ScrollHeader';
import MotionDiv from '../components/MotionDiv';

const SecretsDetection = () => {
  const currentStats = [
    { label: "Total Scans", value: "19", description: "secrets detection scans completed", icon: <FiKey /> },
    { label: "Repositories Analyzed", value: "11", description: "repositories under secrets analysis", icon: <FiGitBranch /> },
    { label: "Issues Identified", value: "17", description: "exposed secrets and credentials", icon: <FiAlertTriangle /> },
    { label: "Last Scan", value: "21", description: "hours ago", icon: <FiTrendingUp /> }
  ];

  const coreFeatures = [
    {
      title: "Credential Detection",
      icon: <FiKey />,
      features: [
        "API Keys: Detection of exposed API keys and tokens",
        "Database Credentials: Identification of database connection strings and passwords",
        "Cloud Credentials: AWS, Azure, GCP access keys and secrets",
        "Authentication Tokens: JWT tokens, OAuth secrets, and session tokens"
      ]
    },
    {
      title: "Pattern Recognition",
      icon: <FiEye />,
      features: [
        "Regex-Based Detection: Advanced regular expression pattern matching",
        "Machine Learning: ML-based pattern recognition for unknown secret types",
        "Context Analysis: Contextual analysis to reduce false positives",
        "Custom Patterns: Support for organization-specific secret patterns"
      ]
    },
    {
      title: "Historical Analysis",
      icon: <FiGitBranch />,
      features: [
        "Git History Scanning: Analysis of entire Git commit history",
        "Branch Analysis: Scanning across all repository branches",
        "Tag Analysis: Analysis of Git tags and releases",
        "Deleted Content: Detection of secrets in deleted files and commits"
      ]
    }
  ];

  const supportedSecretTypes = [
    {
      category: "Authentication Credentials",
      types: [
        "Username/Password Combinations: Traditional authentication credentials",
        "SSH Keys: Private SSH keys and certificates",
        "SSL/TLS Certificates: Private keys and certificate files",
        "Kerberos Tickets: Kerberos authentication tokens"
      ]
    },
    {
      category: "API and Service Keys",
      types: [
        "REST API Keys: Generic REST API authentication keys",
        "GraphQL Tokens: GraphQL API authentication tokens",
        "Webhook Secrets: Webhook validation secrets",
        "Service Account Keys: Service account authentication credentials"
      ]
    },
    {
      category: "Cloud Provider Secrets",
      types: [
        "AWS: Access keys, secret keys, session tokens",
        "Azure: Subscription keys, connection strings, certificates",
        "Google Cloud: Service account keys, API keys",
        "Other Providers: DigitalOcean, Heroku, and other cloud providers"
      ]
    }
  ];

  const bestPractices = [
    {
      category: "Prevention Strategies",
      practices: [
        "Developer Training: Train developers on secure coding practices",
        "Pre-commit Scanning: Implement pre-commit secret scanning",
        "Secret Management: Use proper secret management solutions",
        "Code Reviews: Include secret detection in code review processes",
        "Policy Enforcement: Enforce organizational secret management policies"
      ]
    },
    {
      category: "Detection and Response",
      practices: [
        "Continuous Scanning: Implement continuous secret scanning",
        "Rapid Response: Establish rapid response procedures for exposed secrets",
        "Secret Rotation: Implement automatic secret rotation procedures",
        "Incident Documentation: Document all secret exposure incidents",
        "Lessons Learned: Capture and apply lessons learned"
      ]
    }
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <ScrollHeader title="Secrets Detection" />
        
        {/* Hero Section */}
        <MotionDiv delay={0.1} variant="fadeIn">
          <div className="text-center mb-12">
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto">
              The Secrets Detection module in Ana Security provides comprehensive scanning and monitoring capabilities to identify exposed credentials, API keys, tokens, and other sensitive information in source code repositories, configuration files, and documentation.
            </p>
            
            <MotionDiv delay={0.2} variant="scale">
              <div className="mb-8">
                <img 
                  src="/screenshots/security-modules/07_secrets-detection.png" 
                  alt="Secrets Detection Dashboard"
                  className="rounded-lg border border-light-border dark:border-dark-border shadow-lg w-full max-w-4xl mx-auto hover:shadow-xl transition-shadow duration-300"
                />
              </div>
            </MotionDiv>
          </div>
        </MotionDiv>

        {/* Current Statistics */}
        <MotionDiv delay={0.3} variant="slideLeft">
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Secrets Detection Dashboard - Current Statistics
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {currentStats.map((stat, index) => (
                <MotionDiv key={index} delay={0.4 + index * 0.1} variant="bounce" hover>
                  <div className="text-center p-6 bg-light-hover dark:bg-dark-hover rounded-lg">
                    <div className="text-3xl text-primary-500 mb-3 flex justify-center">
                      {stat.icon}
                    </div>
                    <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {stat.label}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {stat.description}
                    </div>
                  </div>
                </MotionDiv>
              ))}
            </div>

            <div className="bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Dashboard Features
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>New Scan Button:</strong> Orange "New Scan" button for initiating new secrets detection scans</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Project Filtering:</strong> "All" dropdown for filtering by specific projects</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Repository Links:</strong> Direct access to analyzed repositories</span>
                </div>
                <div className="flex items-start gap-3">
                  <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300"><strong>Scan History:</strong> Comprehensive tracking of all scanning activities</span>
                </div>
              </div>
            </div>
          </div>
        </MotionDiv>

        {/* Core Features */}
        <MotionDiv delay={0.2}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Secrets Detection Core Features
            </h2>
            
            <div className="space-y-8">
              {coreFeatures.map((feature, index) => (
                <div key={index} className="border border-light-border dark:border-dark-border rounded-lg p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="text-2xl text-primary-500">
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {feature.title}
                    </h3>
                  </div>
                  <ul className="space-y-3">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700 dark:text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Supported Secret Types */}
        <MotionDiv delay={0.3}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Supported Secret Types
            </h2>
            
            <div className="space-y-8">
              {supportedSecretTypes.map((category, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {category.category}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {category.types.map((type, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-light-hover dark:bg-dark-hover rounded-lg">
                        <FiLock className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Best Practices */}
        <MotionDiv delay={0.4}>
          <div className="bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Secrets Detection Best Practices
            </h2>
            
            <div className="space-y-8">
              {bestPractices.map((category, index) => (
                <div key={index}>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {category.category}
                  </h3>
                  <div className="space-y-3">
                    {category.practices.map((practice, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <FiCheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{practice}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </MotionDiv>

        {/* Call to Action */}
        <MotionDiv delay={0.5}>
          <div className="text-center bg-primary-500/5 dark:bg-primary-500/10 border border-primary-500/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Ready to Secure Your Secrets?
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Start using Secrets Detection to identify and protect exposed credentials in your codebase.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a 
                href="/ana-actions" 
                className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
              >
                <FiCheckCircle className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Use ANA Actions</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Automatically remediate exposed secrets</p>
              </a>
              
              <a 
                href="/dashboard" 
                className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
              >
                <FiTrendingUp className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">View Dashboard</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Monitor your secrets exposure</p>
              </a>
              
              <a 
                href="/integrations" 
                className="block p-6 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-lg hover:border-primary-500/30 transition-colors group"
              >
                <FiSettings className="w-8 h-8 text-primary-500 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Setup Integrations</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Connect your repositories</p>
              </a>
            </div>
          </div>
        </MotionDiv>
      </motion.div>
    </div>
  );
};

export default SecretsDetection;
